import React, { useState, useEffect, useRef } from 'react';

import { useRecoilState } from 'recoil';
import { partnerMeetStateAtom, partnerMeetConfigAtom, meetTypeAtom, tokenAtom } from 'core/config/atoms';

import { ScrollArea } from "@/components/ui/scroll-area";

import { SwitchCamera, Video, VideoOff, Mic, MicOff, Send, Phone, MessageSquareMore, CircleX, UserRoundPlus, Paperclip } from 'lucide-react';

import moment from 'moment';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import { isMobile } from 'react-device-detect';
import axios from 'axios';

import { Device } from 'mediasoup-client';

import socket from 'core/config/socket';

import CustomeTimer from 'core/widgets/components/CustomeTimer';
import WalletTimer from 'core/widgets/components/WalletTimer';

import ServiceHelp from 'services/serviceHelp';
import CustomeChatBubble from 'core/widgets/components/CustomeChatBubble';
import CustomeRemoteVideo from 'core/widgets/components/CustomeRemoteVideo';
import { Dialog } from '@headlessui/react';
import { API_URL } from 'services/models/AppConstent';

export default function PanelMeet() {

    const didInit = useRef(false);

    const [meetConfig, setMeetConfig] = useRecoilState(partnerMeetConfigAtom);
    const [, setMeetState] = useRecoilState(partnerMeetStateAtom);
    const [token] = useRecoilState(tokenAtom);

    const [meetType] = useRecoilState(meetTypeAtom);

    const [mDevice, setMDevice] = useState(null);
    const [prdTransport, setProducerTransport] = useState(null);
    const [conTransport, setConsumerTransport] = useState(null);

    const [prdAudio, setPrdAudio] = useState(null);
    const [prdVideo, setPrdVideo] = useState(null);

    const [consumerStreams, setConsumerStreams] = useState({});
    const [help, setHelp] = useState({});

    const [queuePosition, setQueuePosition] = useState(null);

    const [videoDevices, setVideoDevices] = useState([]);
    const [chats, setChats] = useState([]);

    const [startTime, setStartTime] = useState(new Date());

    const [roomid, setRoomID] = useState('');
    const [digTitle, setDigTitle] = useState('');
    const [digMsg, setDigMsg] = useState('');

    const [isStarted, setIsStarted] = useState(false);
    const [isJoin, setIsJoin] = useState(false);
    const [isVid, setIsVid] = useState(true);
    const [isAudio, setIsAudio] = useState(true);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isPWait, setIsPWait] = useState(false);
    const [isIWait, setIsIWait] = useState(false);
    const [isError, setIsError] = useState(false);

    const [camIndex, setCamIndex] = useState(0);
    const [, setMClock] = useState(0);

    const localStreamRef = useRef();
    const chatRef = useRef('');
    const fileRef = useRef(null);

    const helpService = new ServiceHelp();

    const joinQueue = () => {
        let room = { ...meetConfig };
        socket.emit('createRoom', { room }, (rmid, position) => {
            setQueuePosition(position);
            setMeetConfig({ ...meetConfig, roomid: rmid });
            setRoomID(rmid);
            setIsStarted(true);
            initTransports(rmid);
        });
    }

    const initTransports = async (rmid) => {
        // init producerTransport
        {
            socket.emit('createWebRtcTransport', { roomid: rmid }, (paramsA) => {
                if (paramsA.error) {
                    console.error(paramsA.error)
                    return
                }

                let producerTransport = mDevice.createSendTransport(paramsA);

                producerTransport.on('connect', async function ({ dtlsParameters }, callback, errback) {
                    socket.emit('connectTransport', { roomid: rmid, transport_id: paramsA.id, dtlsParameters }, (res) => {
                        console.log(res);
                    });
                    callback();
                }.bind(this));

                producerTransport.on('produce', async function ({ kind, rtpParameters }, callback, errback) {
                    await socket.emit('produce', {
                        roomid: rmid,
                        producerTransportId: producerTransport.id,
                        kind,
                        rtpParameters
                    }, (res) => {
                        callback({
                            id: res.producer_id
                        });
                    });
                }.bind(this));

                producerTransport.on('connectionstatechange', function (state) {
                    console.log(state);
                    switch (state) {
                        case 'failed':
                            producerTransport.close();
                            break;
                        default:
                            break;
                    }
                }.bind(this));

                setProducerTransport(producerTransport);

                // init consumerTransport
                {
                    socket.emit('createWebRtcTransport', { roomid: rmid }, (paramsB) => {
                        if (paramsB.error) {
                            console.error(paramsB.error)
                            return
                        }

                        let consumerTransport = mDevice.createRecvTransport(paramsB);

                        consumerTransport.on('connect', function ({ dtlsParameters }, callback, errback) {
                            socket.emit('connectTransport', { roomid: rmid, transport_id: consumerTransport.id, dtlsParameters }, (res) => {
                                console.log(res);
                            });
                            callback();
                        }.bind(this));

                        consumerTransport.on('connectionstatechange', async function (state) {
                            console.log('consume state: ', state);
                            switch (state) {
                                case 'failed':
                                    consumerTransport.close();
                                    break;
                                default:
                                    break;
                            }
                        }.bind(this));

                        setConsumerTransport(consumerTransport);

                        socket.emit('getProducers', { roomid: rmid });
                    });
                }
            });
        }
    }

    const sendChat = () => {
        const message = chatRef.current.value;
        if (message !== "") {
            let chat = {
                userid: "host",
                name: meetConfig.name,
                message: message,
                time: moment().toISOString(),
                type: 'text',
            }
            socket.emit('sendMessage', { roomid: roomid, chat });
            chatRef.current.value = "";
        }
    }

    const sendFile = async (file) => {
        const allowedTypes = ['image/png', 'image/jpeg', 'application/pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'video/mp4', 'video/quicktime'];
        if (!allowedTypes.includes(file.type)) {
            alert("File type not allowed");
            return;
        }
        const formData = new FormData();
        formData.append("path", 'temp');
        formData.append("doc", file);
        let tempId = Date.now();
        let tempChat = {
            tempId,
            userid: "host",
            name: meetConfig.name,
            message: "",
            file: file,
            filename: file.name,
            mimetype: file.type,
            time: moment().toISOString(),
            type: 'uploading',
            progress: 0
        };
        setChats((prev) => [...prev, tempChat]);
        try {
            var url = `${API_URL}chat/uploadFile`;
            var requestHeaders = {
                'Content-type': 'multipart/form-data',
                'Accept': '*/*',
                'Authorization': `bearer ${token}`,
            };
            const responseFile = await axios.post(url, formData, {
                headers: requestHeaders,
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    setChats((prev) =>
                        prev.map((c) => c.tempId === tempId ? { ...c, progress: progress } : c)
                    );
                },
            });
            let resFile = responseFile.data.data;
            let chat = {
                userid: "host",
                name: meetConfig.name,
                message: resFile.fileurl,
                filename: file.name,
                mimetype: file.type,
                time: moment().toISOString(),
                type: 'file',
            };
            setChats((prev) => prev.filter((c) => c.tempId !== tempId));
            socket.emit('sendMessage', { roomid, chat });
        } catch (err) {
            toast.warn('File upload failed.', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
        }
    };

    const initMeet = async () => {
        let body = {
            "path": '/interpreterWait'
        };
        var res = await helpService.getHelpByFilter(body, '');
        if (res.status) {
            setHelp(res.data);
        }

        //Todo: add help section for interpriter & partner

        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        localStreamRef.current.srcObject = stream;
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = devices.filter(device => device.kind === "videoinput");
        setVideoDevices(videoInputs);
        socket.emit('initHost', {}, (rtpCapabilities) => {
            initMediaSoup(rtpCapabilities);
        });
    }

    const initMediaSoup = async (rtpCapabilities) => {
        try {
            let dev = new Device();
            await dev.load({
                routerRtpCapabilities: rtpCapabilities
            });
            setMDevice(dev);
        } catch (error) {
            console.log(error);
            if (error.name === 'UnsupportedError') {
                console.warn('browser not supported');
            }
        }
    }

    const getLocalCss = () => {
        if (!isJoin) {
            return 'col-span-12';
        } else {
            if (Object.keys(consumerStreams).length > 1) {
                return 'col-span-12 xl:col-span-3 flex xl:block';
            } else {
                return 'absolute w-[22%] right-[20px] bottom-[20px] z-10';
            }
        }
    }

    const toggleVideo = () => {
        if (prdVideo) {
            if (isVid) {
                prdVideo.pause();
            } else {
                prdVideo.resume();
            }
            setIsVid(!isVid);
        }
    }

    const toggleAudio = () => {
        if (prdAudio) {
            if (isAudio) {
                prdAudio.pause();
            } else {
                prdAudio.resume();
            }
            setIsAudio(!isAudio);
        }
    }

    const toggleCamera = async () => {
        try {
            prdVideo.pause();
            let nextCamIndex = 0;
            if (camIndex === 0) {
                nextCamIndex = (camIndex + 1) % videoDevices.length;
            }
            setCamIndex(nextCamIndex);

            const constraints = {
                video: {
                    deviceId: { exact: videoDevices[nextCamIndex].deviceId },
                },
            };

            const newStream = await navigator.mediaDevices.getUserMedia(constraints);
            const oldStream = localStreamRef.current.srcObject;

            const oldVideoTrack = oldStream.getVideoTracks()[0];
            const newVideoTrack = newStream.getVideoTracks()[0];

            await prdVideo.replaceTrack({ track: newVideoTrack });

            oldStream.removeTrack(oldVideoTrack);
            oldVideoTrack.stop();

            oldStream.addTrack(newVideoTrack);
            localStreamRef.current.srcObject = oldStream;
            prdVideo.resume();
        } catch (err) {
            console.error("Error switching camera:", err);
        }
    }

    const endCall = () => {
        socket.emit('endRoom', { roomid }, () => {
            console.log('Room Ended');
        });
    }

    const stopCall = () => {
        setDigTitle('Alert Message');
        setDigMsg('Ending call, Your top up balance has been finished please top the balance and rejoin again!');
        setIsError(true);
        socket.emit('endRoom', { roomid }, () => {
            console.log('Room Ended');
        });
    }

    const warnCall = () => {
        setDigTitle('Alert Message');
        setDigMsg('your top up balance is running low!');
        setIsError(true);
    }

    const reset = () => {
        setMeetState(2);
    }

    const connectSendTransport = () => {
        connectAudio();
        connectVideo();
    }

    const connectAudio = async () => {
        const track = localStreamRef.current.srcObject.getAudioTracks()[0];
        const params = { track };
        let producer = await prdTransport.produce(params);

        producer.on('trackended', () => {
            const producer_id = prdAudio.id;
            socket.emit('producerClosed', {
                producer_id
            });
            prdAudio.close();
            setPrdAudio(null);
        });

        producer.on('transportclose', () => {
            console.log('Producer transport close')
            setPrdAudio(null);
        });

        producer.on('close', () => {
            console.log('Closing producer')
            setPrdAudio(null);
        });

        setPrdAudio(producer);
    }

    const connectVideo = async () => {
        const track = localStreamRef.current.srcObject.getVideoTracks()[0];
        const params = { track };
        let producer = await prdTransport.produce(params);

        producer.on('trackended', () => {
            const producer_id = prdVideo.id;
            socket.emit('producerClosed', {
                producer_id
            });
            prdVideo.close();
            setPrdVideo(null);
        });

        producer.on('transportclose', () => {
            console.log('Producer transport close')
            setPrdVideo(null);
        });

        producer.on('close', () => {
            console.log('Closing producer')
            setPrdVideo(null);
        });

        setPrdVideo(producer);
    }

    const getConsumeStream = async (producerId, sid) => {
        const { rtpCapabilities } = mDevice;
        socket.emit('consume', { roomid, rtpCapabilities, consumerTransportId: conTransport.id, producerId }, async (param) => {
            const { id, kind, rtpParameters } = param;
            let codecOptions = {}
            const consumer = await conTransport.consume({
                id,
                producerId,
                kind,
                rtpParameters,
                codecOptions
            });

            if (consumerStreams[sid] === undefined) {
                consumerStreams[sid] = new MediaStream();
            }
            consumerStreams[sid].addTrack(consumer.track);

            setConsumerStreams(consumerStreams);
            setMClock(prev => prev + 1);
        });
    }

    useEffect(() => {
        if (didInit.current) return;
        didInit.current = true;
        initMeet();
        return () => {
            console.log("here");
        };
    }, []);

    useEffect(() => {
        socket.on('queuePosition', (position) => {
            console.log(position);
            console.log(roomid);
            setQueuePosition(position.pos);
            if (position.status === "pwait") {
                setIsPWait(true);
            }
            if (position.status === "iwait") {
                setIsIWait(true);
            }
            if (position.status === "live") {
                if (position.mid === roomid) {
                    setStartTime(moment(position.calltime).toDate());
                    setIsJoin(true);
                }
            }
        });
        socket.on('recvChat', (chat) => {
            let chatList = [...chats];
            chatList.push(chat);
            setChats(chatList);
        });
        return () => {
            endCall();
        };
    }, [chats, roomid]);

    useEffect(() => {
        if (mDevice !== null && roomid === '') {
            joinQueue();
        }
    }, [mDevice]);

    useEffect(() => {
        if (roomid !== '') {
            socket.on('endedRooms', ({ romid, sts }) => {
                if (roomid === romid) {
                    reset();
                }
            });
        }
    }, [roomid, isJoin]);

    useEffect(() => {
        if (prdTransport !== null) {
            connectSendTransport();
        }
    }, [prdTransport]);

    useEffect(() => {
        if (conTransport !== null && mDevice !== null) {
            socket.on('newProducers', async function (data) {
                if (data.length !== 0) {
                    let { producer_id, producer_socket_id } = data;
                    if (producer_socket_id) {
                        await getConsumeStream(producer_id, producer_socket_id);
                    }
                }
            }.bind(this));
        }
    }, [conTransport, mDevice]);

    return (
        <>
            <Dialog open={isError} onClose={() => { setIsError(false) }} className='overflow-y-auto overflow-x-hidden z-[100] w-full min-h-screen fixed top-0 left-0 bg-black bg-opacity-70 flex items-center justify-center'>
                <div className="w-[90%] md:w-[40%] bg-white shadow-lg rounded-md p-4">
                    <div className='flex items-center justify-between'>
                        <h3 className="text-lg font-semibold text-gray-900">
                            {digTitle} &nbsp;
                        </h3>
                    </div>
                    <hr className='mt-1' />
                    <h3 className="text-base text-gray-900 py-2">
                        {digMsg} &nbsp;
                    </h3>
                    <div className="px-2 py-3 text-right" onClick={() => {
                        setDigMsg('');
                        setDigTitle('');
                        setIsError(false);
                    }}>
                        <button type="button" className="p-1 text-sm font-medium text-prime">
                            OK
                        </button>
                    </div>
                </div>
            </Dialog>
            {
                isChatOpen && <div className='overflow-y-auto overflow-x-hidden z-50 w-full h-modal md:h-full fixed top-0 left-0 bg-black bg-opacity-70'>
                    <div className='bg-white w-full h-screen p-2'>
                        <div className='flex flex-col bg-white'>
                            <div className='px-2 flex items-center justify-between'>
                                <span className='text-lg font-medium'>Chats</span>
                                <div className='w-[20px] h-[20px] flex items-center justify-center' onClick={() => { setIsChatOpen(false) }}>
                                    <CircleX size={18} />
                                </div>
                            </div>
                            <div className='h-[1px] bg-gray-900' ></div>
                            <ScrollArea className="w-full min-h-[80vh] flex-grow bg-gray-100 shadow-inner my-2 p-2 rounded-md">
                                {
                                    chats.map((chat, index) => {
                                        const next = chats[index + 1];
                                        const isLastInGroup = !next || next.userid !== chat.userid;
                                        return <CustomeChatBubble chat={chat} currentUserId="host" isLastInGroup={isLastInGroup} />
                                    })
                                }
                            </ScrollArea>
                            <div className="flex items-center gap-2 mt-1">
                                <input type="text" placeholder="Your Message" disabled={!isStarted} className="w-full h-10 rounded-md bg-gray-200 px-4 py-2 text-sm font-sans" autoComplete="off" ref={chatRef} />
                                <input type="file" ref={fileRef} className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => {
                                    const file = e.target.files[0]; if (file) sendFile(file);
                                }} />
                                <button className='w-10 h-10 bg-gray-300 rounded-md text-black flex items-center justify-center' onClick={() => {
                                    if (isStarted) {
                                        fileRef.current.click();
                                    }
                                }} title="Attach File">
                                    <Paperclip size={14} />
                                </button>
                                <button className='w-10 h-10 bg-prime ml-1 rounded-md text-white flex items-center justify-center' onClick={sendChat}>
                                    <Send size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            }
            <div className='w-full h-full grid grid-cols-12 gap-4'>
                <div className='col-span-12 xl:col-span-8'>
                    <div className='w-full h-full flex xl:items-center xl:justify-center'>
                        <div className='w-full'>
                            <div className='grid grid-cols-12 gap-4 relative'>
                                {
                                    isJoin && Object.keys(consumerStreams).length === 1 && <div className="col-span-12 pt-[120%] xl:pt-[56.25%] border-2 border-prime rounded-2xl bg-black">
                                        <CustomeRemoteVideo stream={consumerStreams[Object.keys(consumerStreams)[0]]} name="IN" css="object-contain absolute inset-0 w-full h-full" />
                                    </div>
                                }
                                {
                                    isJoin && Object.keys(consumerStreams).length > 1 && <div className="col-span-12 xl:hidden border-2 border-prime rounded-2xl bg-black">
                                        <div className='w-full pt-[100%] xl:pt-[56.25%] relative'>
                                            <CustomeRemoteVideo stream={consumerStreams[Object.keys(consumerStreams)[0]]} name="IN" css="object-contain absolute inset-0 w-full h-full" />
                                        </div>
                                    </div>
                                }
                                <div className={`${getLocalCss()}`}>
                                    <div className={`w-full ${isJoin ? Object.keys(consumerStreams).length === 1 ? "pt-[160%] xl:pt-[56.25%]" : "mx-1 xl:mx-0 my-2 xl:my-0 pt-[30%] xl:pt-[56.25%]" : "pt-[56.25%]"} relative`}>
                                        <video ref={localStreamRef} autoPlay playsInline muted className="border-2 border-prime rounded-2xl object-cover xl:object-fill absolute inset-0 w-full h-full transform scale-x-[-1]" />
                                        {
                                            (!isJoin && Object.keys(help).length !== 0) && <video src={help.videoFile.fileurl} autoPlay playsInline loop crossOrigin="anonymous" className='border-2 border-prime rounded-2xl object-cover xl:object-fill absolute inset-0 w-full h-full transform'>
                                                <track label="English" kind="subtitles" src={help.srtFile.fileurl} srcLang="en" default />
                                            </video>
                                        }
                                    </div>
                                    {
                                        isJoin && Object.keys(consumerStreams).length > 1 && Object.keys(consumerStreams).map((key, index) => {
                                            if (index === 0) return <div></div>;
                                            return <div className='w-full pt-[30%] mx-1 xl:mx-0 xl:pt-[56.25%] relative my-2 border-2 border-prime rounded-2xl bg-black'>
                                                <CustomeRemoteVideo stream={consumerStreams[Object.keys(consumerStreams)[key]]} name="IN" css="object-contain absolute inset-0 w-full h-full" />
                                            </div>;
                                        })
                                    }
                                </div>
                                {
                                    isJoin && Object.keys(consumerStreams).length > 1 && <div className="col-span-9 hidden xl:block border-2 border-prime rounded-2xl bg-black">
                                        <div className='w-full pt-[100%] xl:pt-[56.25%] relative'>
                                            <CustomeRemoteVideo stream={consumerStreams[Object.keys(consumerStreams)[0]]} name="IN" css="object-contain absolute inset-0 w-full h-full" />
                                        </div>
                                    </div>
                                }
                            </div>
                            <div className='w-full h-[60px] mt-4'>
                                <div className={`flex items-center ${Object.keys(consumerStreams).length > 1 ? "justify-center xl:justify-end" : "justify-center"}`}>
                                    <div className={`w-10 h-10 cursor-pointer rounded-2xl mx-2 text-white flex items-center justify-center bg-gray-800`} onClick={() => {
                                        toggleCamera();
                                    }}>
                                        <SwitchCamera size={18} />
                                    </div>
                                    <div className={`w-10 h-10 cursor-pointer rounded-2xl mx-2 text-white flex items-center justify-center ${isVid ? 'bg-gray-800' : 'bg-red-600'}`} onClick={() => {
                                        if (prdTransport !== null) {
                                            toggleVideo();
                                        }
                                    }}>
                                        {isVid && <Video size={18} />}
                                        {!isVid && <VideoOff size={18} />}
                                    </div>
                                    <div className={`w-10 h-10 cursor-pointer rounded-2xl mx-2 text-white flex items-center justify-center ${isAudio ? 'bg-gray-800' : 'bg-red-600'}`} onClick={() => {
                                        if (prdTransport !== null) {
                                            toggleAudio();
                                        }
                                    }}>
                                        {isAudio && <Mic size={18} />}
                                        {!isAudio && <MicOff size={18} />}
                                    </div>
                                    <div className='w-10 h-10 cursor-pointer rounded-2xl mx-2 text-white flex items-center justify-center bg-gray-800 xl:hidden' onClick={() => {
                                        if (isJoin) {
                                            setIsChatOpen(true);
                                        }
                                    }}>
                                        <MessageSquareMore size={18} />
                                    </div>
                                    <CopyToClipboard text={`https://app.ishcallsign.com/invite/` + roomid} onCopy={() => {
                                        if (roomid !== '') {
                                            toast.info('Meeting invite copied to clipboard.', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                                            if (isMobile) {
                                                console.log(`shareMsg:https://app.ishcallsign.com/invite/` + roomid);
                                            }
                                        }
                                    }}>
                                        <div className='w-10 h-10 cursor-pointer rounded-2xl mx-2 text-white flex items-center justify-center bg-gray-800'>
                                            <UserRoundPlus size={18} />
                                        </div>
                                    </CopyToClipboard>
                                    <div className='w-10 h-10 cursor-pointer rounded-2xl mx-2 text-white flex items-center justify-center bg-red-600' onClick={() => {
                                        endCall();
                                    }}>
                                        <Phone size={18} />
                                    </div>
                                    {(() => {
                                        switch (meetType) {
                                            case '1':
                                            case '2':
                                                return <div className='hidden xl:block'>
                                                    {(Object.keys(consumerStreams).length > 0 && isJoin) && <WalletTimer stop={stopCall} warn={warnCall} />}
                                                </div>
                                            case '3':
                                            case '4':
                                                return <div className='hidden xl:block'>
                                                    {Object.keys(consumerStreams).length > 0 && <CustomeTimer isJoin={isJoin} stime={startTime} />}
                                                </div>
                                        }
                                    })()}
                                </div>
                                {(() => {
                                    switch (meetType) {
                                        case '1':
                                        case '2':
                                            return <div className='xl:hidden mt-4'>
                                                {(Object.keys(consumerStreams).length > 0 && isJoin) && <WalletTimer stop={stopCall} warn={warnCall} />}
                                            </div>
                                        case '3':
                                        case '4':
                                            return <div className='xl:hidden mt-4'>
                                                {Object.keys(consumerStreams).length > 0 && <CustomeTimer isJoin={isJoin} stime={startTime} />}
                                            </div>
                                    }
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
                {
                    (isStarted && !isJoin) && <div className='col-span-12 xl:col-span-4 p-2 flex flex-col items-center xl:justify-center'>
                        <div className='w-full bg-gray-50 rounded-xl border border-dashed border-prime p-4'>
                            <h1 className='text-2xl text-center font-medium'>Please Wait</h1>
                            {(!isPWait && !isIWait) && <h1 className='text-sm text-prime text-center mt-6 font-medium'>Your position in queue is <u> {queuePosition === 1 ? "Next" : queuePosition} in line</u></h1>}
                            {isPWait && <h1 className='text-sm text-prime text-center mt-6 font-medium'>Wating for partner to join</h1>}
                            {isIWait && <h1 className='text-sm text-prime text-center mt-6 font-medium'>Wating for interpreter to join</h1>}
                        </div>
                    </div>
                }
                {
                    isJoin && <div className='col-span-4 py-4 hidden xl:block'>
                        <div className='bg-gray-50 h-full max-h-[60vh] rounded-xl flex flex-col p-2 border'>
                            <div className='px-2'>
                                <span className='text-lg font-medium'>Chats</span>
                            </div>
                            <div className='h-[1px] bg-gray-900' ></div>
                            <ScrollArea className="w-full min-h-[40vh] flex-grow bg-gray-100 shadow-inner my-2 p-2 rounded-md">
                                {
                                    chats.map((chat, index) => {
                                        const next = chats[index + 1];
                                        const isLastInGroup = !next || next.userid !== chat.userid;
                                        return <CustomeChatBubble chat={chat} currentUserId="host" isLastInGroup={isLastInGroup} />
                                    })
                                }
                            </ScrollArea>
                            <div className="flex items-center gap-2 mt-1">
                                <input type="text" placeholder="Your Message" disabled={!isStarted} className="w-full h-10 rounded-md bg-gray-200 px-4 py-2 text-sm font-sans" autoComplete="off" ref={chatRef} />
                                <input type="file" ref={fileRef} className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => {
                                    const file = e.target.files[0]; if (file) sendFile(file);
                                }} />
                                <button className='w-10 h-10 bg-gray-300 rounded-md text-black flex items-center justify-center' onClick={() => {
                                    if (isStarted) {
                                        fileRef.current.click();
                                    }
                                }} title="Attach File">
                                    <Paperclip size={14} />
                                </button>
                                <button className='w-10 h-10 bg-prime ml-1 rounded-md text-white flex items-center justify-center' onClick={sendChat}>
                                    <Send size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                }
            </div>
        </>
    )
}
